/**
 * Ashrafie display formatters.
 *
 * Pure presentation helpers shared by the dashboard wallet and the global
 * header coin chip. Numbers in, strings out, no React, no side effects.
 *
 * BigInt note: balance fields arrive as digit strings (precision beyond
 * Number.MAX_SAFE_INTEGER). These helpers degrade safely on junk input.
 *
 * @module lib/api/ashrafie/format
 */

import { formatCents } from '@lib/currency';
import { ASHRAFIE_CONVERSION_POINTS_PER_PENNY } from './constants';

const SAFE_MAX = Number.MAX_SAFE_INTEGER;

/** Coerce a possibly-string/bigint value into a finite display number (0 on junk). */
function toDisplayNumber(
  value: string | bigint | number | undefined | null
): number {
  if (value === undefined || value === null) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export interface FormatPointsOptions {
  /** BCP-47 locale for grouping separators. Defaults to the runtime locale. */
  locale?: string;
  /** Collapse very large values to compact notation (1.2M) to protect tight layouts. */
  compact?: boolean;
}

/**
 * Format an Ashrafie points balance with locale-aware grouping.
 * Values beyond Number.MAX_SAFE_INTEGER are returned verbatim (precision-safe).
 *
 * @example formatPoints('1250')                 // '1,250'
 * @example formatPoints('1500000', { compact: true }) // '1.5M'
 */
export function formatPoints(
  value: string | bigint | number | undefined | null,
  options?: FormatPointsOptions
): string {
  if (value === undefined || value === null) return '0';

  // Precision guard: an ultra-large integer string stays exact rather than
  // losing digits through Number(). Non-numeric junk collapses to '0'.
  if (typeof value === 'string') {
    const isInt = /^-?\d+$/.test(value);
    if (!isInt) return '0';
    if (Number(value) > SAFE_MAX) return value;
  }

  const numeric = toDisplayNumber(value);
  if (options?.compact && Math.abs(numeric) >= 1_000_000) {
    return new Intl.NumberFormat(options.locale, {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(numeric);
  }
  return new Intl.NumberFormat(options?.locale).format(numeric);
}

/**
 * Convert a points balance into the charity value it can fund, formatted as GBP.
 * Ashrafie earn + redemption are penny-denominated (1 point == 1 penny by
 * default), so points map straight onto pence before formatCents divides by 100.
 *
 * @example pointsToGiveValue('1250') // '£12.50'
 */
export function pointsToGiveValue(
  value: string | bigint | number | undefined | null,
  options?: { locale?: string }
): string {
  const points = toDisplayNumber(value);
  const pence = points / ASHRAFIE_CONVERSION_POINTS_PER_PENNY;
  return formatCents(pence, { currency: 'GBP', locale: options?.locale });
}

/**
 * Best-effort points magnitude for a history row. The backend does not hoist the
 * entry amount onto GET /account/history, so we read it defensively from
 * metadata and return null when absent. Callers must show no delta rather than a
 * fabricated number when this is null.
 */
export function extractTxnPoints(
  metadata: Record<string, unknown> | undefined | null
): number | null {
  if (!metadata) return null;
  const candidate =
    (metadata.points as unknown) ??
    (metadata.pointsEarned as unknown) ??
    (metadata.pointsSpent as unknown) ??
    (metadata.amount as unknown);
  if (candidate === undefined || candidate === null) return null;
  const numeric = Number(candidate);
  return Number.isFinite(numeric) ? numeric : null;
}

/**
 * Compact, locale-aware relative time ("now", "3h ago", "2d ago") for tight
 * surfaces. Falls back to a localized date past ~30 days.
 */
export function formatRelativeTime(iso: string, locale?: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';

  const diffSec = Math.round((then - Date.now()) / 1000); // negative == past
  const abs = Math.abs(diffSec);
  const rtf = new Intl.RelativeTimeFormat(locale, {
    numeric: 'auto',
    style: 'narrow',
  });

  const MIN = 60;
  const HOUR = 60 * MIN;
  const DAY = 24 * HOUR;

  if (abs < MIN) return rtf.format(0, 'second');
  if (abs < HOUR) return rtf.format(Math.round(diffSec / MIN), 'minute');
  if (abs < DAY) return rtf.format(Math.round(diffSec / HOUR), 'hour');
  if (abs < 30 * DAY) return rtf.format(Math.round(diffSec / DAY), 'day');
  return new Date(iso).toLocaleDateString(locale);
}
